/**
 * Format date to dd-mm-yyyy format regardless of browser locale
 * @param date - Date object, timestamp (number), or date string
 * @returns Formatted date string in dd-mm-yyyy format
 */
export const formatDateDDMMYYYY = (date: Date | number | string): string => {
  let dateObj: Date;
  
  if (date instanceof Date) {
    dateObj = date;
  } else if (typeof date === 'number') {
    dateObj = new Date(date);
  } else {
    dateObj = new Date(date);
  }
  
  // Check if date is valid
  if (isNaN(dateObj.getTime())) {
    return '—';
  }
  
  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const year = dateObj.getFullYear();
  
  return `${day}-${month}-${year}`;
};

/**
 * Format date and time to dd-mm-yyyy HH:MM:SS format regardless of browser locale
 * @param date - Date object, timestamp (number), or date string
 * @returns Formatted date-time string in dd-mm-yyyy HH:MM:SS format
 */
export const formatDateTimeDDMMYYYY = (date: Date | number | string): string => {
  let dateObj: Date;
  
  if (date instanceof Date) {
    dateObj = date;
  } else if (typeof date === 'number') {
    dateObj = new Date(date);
  } else {
    dateObj = new Date(date);
  }
  
  // Check if date is valid
  if (isNaN(dateObj.getTime())) {
    return '—';
  }
  
  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const year = dateObj.getFullYear();
  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');
  const seconds = String(dateObj.getSeconds()).padStart(2, '0');
  
  return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
};

/**
 * Format date to dd-mm-yy format (2-digit year) regardless of browser locale
 * @param date - Date object, timestamp (number), or date string
 * @returns Formatted date string in dd-mm-yy format
 */
export const formatDateDDMMYY = (date: Date | number | string): string => {
  let dateObj: Date;
  
  if (date instanceof Date) {
    dateObj = date;
  } else if (typeof date === 'number') {
    dateObj = new Date(date);
  } else {
    dateObj = new Date(date);
  }
  
  // Check if date is valid
  if (isNaN(dateObj.getTime())) {
    return '—';
  }
  
  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const year = String(dateObj.getFullYear()).slice(-2);
  
  return `${day}-${month}-${year}`;
};

/**
 * Get today's date in YYYY-MM-DD (local timezone) for use as max on type="date" inputs to disable future dates.
 */
export const getMaxDateToday = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Get a future date in YYYY-MM-DD (e.g. 2 years from now) for use as max on type="date" inputs that allow future (e.g. booking date filter).
 */
export const getMaxDateFuture = (yearsFromNow: number = 2): string => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + yearsFromNow);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Get max end date for a custom range: startDate + maxDays (default 31).
 * Use for End Date input when custom date range is limited to 31 days.
 * @param startDateStr - YYYY-MM-DD
 * @param maxDays - default 31
 */
export const getMaxEndDateForStart = (startDateStr: string, maxDays: number = 31): string => {
  if (!startDateStr || !/^\d{4}-\d{2}-\d{2}$/.test(startDateStr.trim())) return '';
  const [y, m, d] = startDateStr.trim().split('-').map(Number);
  const start = new Date(y, m - 1, d);
  start.setDate(start.getDate() + maxDays);
  return `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
};

/**
 * Get current date-time in YYYY-MM-DDTHH:mm (local timezone) for use as max on type="datetime-local" inputs to disable future dates.
 */
export const getMaxDatetimeLocal = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/**
 * Format time to HH:mm (24-hour) regardless of browser locale
 * @param date - Date object, timestamp (number), or date string
 * @returns Formatted time string in HH:mm format
 */
export const formatTimeHHmm = (date: Date | number | string): string => {
  let dateObj: Date;

  if (date instanceof Date) {
    dateObj = date;
  } else if (typeof date === 'number') {
    dateObj = new Date(date);
  } else {
    dateObj = new Date(date);
  }

  // Check if date is valid
  if (isNaN(dateObj.getTime())) {
    return '—';
  }

  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');

  return `${hours}:${minutes}`;
};

